package fr.frux.navigation

import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.navigation3.runtime.NavEntry
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.ui.NavDisplay
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import fr.frux.app.resources.Res
import fr.frux.app.resources.nav_addtree_confirmation
import fr.frux.app.resources.nav_addtree_confirmation_first_here
import fr.frux.app.resources.nav_addtree_photo_upload_failed
import fr.frux.app.resources.nav_auth_context_addtree
import fr.frux.app.resources.nav_auth_context_createharvest
import fr.frux.app.resources.nav_auth_context_favorites
import fr.frux.app.resources.nav_auth_context_login
import fr.frux.app.resources.nav_auth_context_profile
import fr.frux.app.resources.nav_tree_deleted
import fr.frux.core.common.config.webAppBaseUrl
import fr.frux.core.common.directions.createDirectionsLauncher
import fr.frux.core.common.platform.platformId
import fr.frux.core.common.share.buildTreeShareContent
import fr.frux.core.common.share.createShareLauncher
import fr.frux.core.designsystem.components.LocalNavigationBackInterceptors
import fr.frux.feature.map.DEFAULT_CAMERA
import fr.frux.feature.map.MapHostState
import fr.frux.feature.map.MapScreen
import fr.frux.feature.map.NewTreeFocus
import fr.frux.feature.map.focusOnSite
import fr.frux.feature.map.revealTree
import fr.frux.splash.EntryBrandVisual
import fr.frux.splash.WelcomeLogo
import kotlinx.coroutines.flow.flowOf
import org.jetbrains.compose.resources.stringResource

/**
 * Sous-titre du mur d'authentification (#264), dérivé de la route qui a demandé la connexion.
 * `null` = pas de contexte particulier à afficher (mur générique).
 */
@Composable
private fun Route?.toAuthContextMessage(): String? =
    when (this) {
        is Route.Favorites -> stringResource(Res.string.nav_auth_context_favorites)
        is Route.AddTree -> stringResource(Res.string.nav_auth_context_addtree)
        is Route.Profile, is Route.MyTrees -> stringResource(Res.string.nav_auth_context_profile)
        is Route.CreateHarvest -> stringResource(Res.string.nav_auth_context_createharvest)
        is Route.TreeDetail, is Route.HarvestDetail -> stringResource(Res.string.nav_auth_context_login)
        is Route.SiteDetail -> stringResource(Res.string.nav_auth_context_login)
        else -> null
    }

/**
 * Métadonnée posée sur les entrées d'onglet : changer d'onglet échange le contenu sans animation.
 * La bottom bar vit au-dessus du NavDisplay (cf. App) et ne bouge pas ; seul un écran poussé
 * (formulaire, détail) glisse par-dessus avec le spec global.
 */
private val tabSwitchMetadata =
    NavDisplay.transitionSpec {
        EnterTransition.None togetherWith ExitTransition.None
    }

/**
 * Maps Route keys to composable screens.
 *
 * This is the Navigation 3 UI equivalent of NavGraphBuilder from Navigation 2.
 * Instead of using composable<T> inside a NavHost, we use entry<T> inside an entryProvider.
 *
 * Key differences from Navigation 2:
 * - No NavController passed to screens (use callbacks instead)
 * - Type-safe route access via lambda parameter
 * - Screens are pure composables (easier to test)
 *
 * Usage:
 * ```kotlin
 * NavDisplay(
 *     backStack = navigationState.backStack,
 *     entryProvider = fruxEntryProvider(navigationState)
 * )
 * ```
 */
@Composable
fun fruxEntryProvider(
    navigationState: NavigationState,
    onOnboardingComplete: () -> Unit = {},
    /**
     * Fin de l'accueil avec la position déclinée (#502) : la carte ne doit ni redemander la
     * position au lancement ni afficher le bandeau de refus à l'arrivée.
     */
    onLocationDeclinedInOnboarding: () -> Unit = {},
    /** Vrai pour la session quand l'accueil s'est terminé sans position (#502), relayé à [MapScreen]. */
    locationSettledByOnboarding: Boolean = false,
    /**
     * Etat de la surface carte persistante (App). Null tant que la carte n'a jamais ete requise
     * (Welcome/Auth) — jamais au moment ou une entree qui s'en sert est composee.
     */
    mapHost: MapHostState? = null,
): (Route) -> NavEntry<Route> =
    entryProvider {
        // ============================================================================
        // WELCOME / ONBOARDING
        // ============================================================================

        entry<Route.Welcome> {
            fr.frux.feature.onboarding.OnboardingScreen(
                // Le desktop n'a aucun provider de position (outil interne, ADR 0003) : la
                // promesse suffit. Seule lecture de plateforme de cet écran, assumée ici et pas
                // dans le module.
                locationStep = platformId != "DESKTOP",
                onFinished = { outcome ->
                    if (outcome == fr.frux.feature.onboarding.OnboardingOutcome.LocationDeclined) {
                        onLocationDeclinedInOnboarding()
                    }
                    // First launch complete: persist it, then browse anonymously
                    onOnboardingComplete()
                    navigationState.replaceWith(Route.Map)
                },
                onLogin = {
                    navigationState.navigate(Route.Auth)
                },
                brandVisual = { EntryBrandVisual() },
                logo = { WelcomeLogo(it) },
            )
        }

        // ============================================================================
        // AUTHENTICATION
        // ============================================================================

        entry<Route.Auth> {
            fr.frux.feature.auth.AuthScreen(
                onAuthSuccess = {
                    // Reset the stack on Map and resume the pending destination;
                    // back must never return to Auth
                    navigationState.onAuthenticated()
                },
                onBrowseAnonymously = {
                    // Welcome → « Se connecter » → « Explorer sans compte » termine aussi
                    // l'onboarding : sans cela le hold publicitaire resterait posé toute la session
                    // et Welcome reviendrait à chaque lancement.
                    onOnboardingComplete()
                    // Après une déconnexion, cet écran est la racine d'une pile vide : `resetTo` et
                    // non `replaceWith`, pour que la carte devienne la nouvelle racine plutôt que de
                    // laisser Auth atteignable par retour arrière (#48).
                    navigationState.resetTo(Route.Map)
                },
                // Mur contextualisé (#264) : feature-auth ne connaît pas Route, le mapping se fait
                // ici, seul appelant à voir à la fois la route en attente et l'écran mur.
                contextMessage = navigationState.pendingRoute.toAuthContextMessage(),
                brandVisual = { EntryBrandVisual() },
                // Seule porte des anonymes vers les pages légales (#468) : Profil les amène ici.
                onOpenAbout = { navigationState.navigate(Route.Settings) },
            )
        }

        // ============================================================================
        // MAIN SCREENS
        // ============================================================================

        entry<Route.Map>(metadata = tabSwitchMetadata) {
            // Chrome de la carte seulement : la vue carte est la surface persistante composée par App
            // sous ce NavDisplay (#124). mapHost est non-null dès que cette entrée est composable.
            mapHost?.let { host ->
                // Un seul launcher pour toute la durée de vie de cette entrée (#335) : le geste
                // lui-même (Intent, URL) n'a aucun état à partager, on évite seulement de le
                // reconstruire à chaque tap.
                val directionsLauncher = remember { createDirectionsLauncher() }
                MapScreen(
                    host = host,
                    onTreeClick = { treeId ->
                        navigationState.navigate(Route.TreeDetail(treeId))
                    },
                    onSiteClick = { siteId -> navigationState.navigate(Route.SiteDetail(siteId)) },
                    onSeeAllClick = { context ->
                        navigationState.navigate(
                            Route.TreeList(
                                latitude = context.latitude,
                                longitude = context.longitude,
                                radiusKm = context.radiusKm,
                                userLatitude = context.userLatitude,
                                userLongitude = context.userLongitude,
                            ),
                        )
                    },
                    onStartHarvest = { treeId ->
                        navigationState.navigate(Route.CreateHarvest(treeId = treeId))
                    },
                    onLogin = { navigationState.navigate(Route.Auth) },
                    onDirections = { latitude, longitude, label ->
                        directionsLauncher.openDirections(latitude, longitude, label)
                    },
                    locationSettledByOnboarding = locationSettledByOnboarding,
                    onAddTree = { guideRead ->
                        navigationState.navigate(
                            addTreeRouteFrom(host.camera.position, AddTreeOrigin.EMPTY_AREA, guideRead),
                        )
                    },
                    onAddAnotherTree = {
                        // La caméra vient d'être centrée sur le nouvel arbre : le suivant part de là.
                        navigationState.navigate(addTreeRouteFrom(host.camera.position, AddTreeOrigin.UNKNOWN))
                    },
                )
            }
        }

        entry<Route.Harvests>(metadata = tabSwitchMetadata + mapPanelMetadata) {
            // La carte persistante (ADR 0009) porte deja le croisement especes x arbres charges
            // (#403) : le reutiliser evite une deuxieme source de verite. `null` seulement si
            // aucune carte n'a jamais ete composee (mapHost cree paresseusement, cf. App.kt) — le
            // repli national du HarvestsViewModel prend alors le relais.
            val mapState = mapHost?.viewModel?.state?.collectAsState()
            // La liste suit la zone de la carte (#534). mapHost n'est jamais nul ici (App compose
            // la carte dès cet onglet), le repli sur la caméra par défaut n'est qu'un filet.
            val camera = mapHost?.camera
            val mapCamera =
                remember(camera) {
                    if (camera == null) flowOf(DEFAULT_CAMERA) else snapshotFlow { camera.position }
                }
            val harvestsViewModel =
                fr.frux.feature.harvests.rememberHarvestsViewModel(
                    mapCamera = mapCamera,
                    recenterMap = { place -> mapHost?.viewModel?.selectPlace(place) },
                )
            fr.frux.feature.harvests.HarvestsScreen(
                viewModel = harvestsViewModel,
                onExploreMap = { navigationState.replaceWith(Route.Map) },
                onHarvestClick = { harvestId -> navigationState.navigate(Route.HarvestDetail(harvestId)) },
                onSeasonSpeciesClick = { species ->
                    mapHost?.viewModel?.onSpeciesSelected(species)
                    navigationState.replaceWith(Route.Map)
                },
                mapSeasonalAround = mapState?.value?.seasonalAround,
            )
        }

        entry<Route.MyTrees>(metadata = mapPanelMetadata) {
            fr.frux.feature.mytrees.MyTreesScreen(
                onAddTree = {
                    navigationState.navigate(addTreeRouteFrom(null, AddTreeOrigin.UNKNOWN))
                },
                onBack = { navigationState.navigateBack() },
                onTreeClick = { treeId -> navigationState.navigate(Route.TreeDetail(treeId)) },
            )
        }

        entry<Route.Favorites>(metadata = tabSwitchMetadata + mapPanelMetadata) {
            fr.frux.feature.favorites.FavoritesScreen(
                onExploreMap = { navigationState.replaceWith(Route.Map) },
                onTreeClick = { treeId -> navigationState.navigate(Route.TreeDetail(treeId)) },
                onSiteClick = { siteId -> navigationState.navigate(Route.SiteDetail(siteId)) },
            )
        }

        entry<Route.Settings>(metadata = fullZoneMetadata) {
            // Sub-page, no bottom nav
            fr.frux.feature.settings.SettingsScreen(
                onBack = { navigationState.navigateBack() },
                onOpenShowcase = { navigationState.navigate(Route.Showcase) },
                onOpenNotificationPreferences = { navigationState.navigate(Route.NotificationPreferences) },
                // Lu à la composition : une déconnexion vide la pile (#48), l'écran ne survit
                // donc pas à un changement de session.
                showAccountSettings = navigationState.isSignedIn,
            )
        }

        entry<Route.Notifications>(metadata = fullZoneMetadata) {
            fr.frux.feature.notifications.NotificationsScreen(
                onBack = { navigationState.navigateBack() },
                onOpenPreferences = { navigationState.navigate(Route.NotificationPreferences) },
                onOpenSubject = { subjectType, subjectId ->
                    when (subjectType) {
                        fr.frux.shared.domain.model.NotificationSubjectType.HARVEST -> {
                            navigationState.navigate(Route.HarvestDetail(subjectId))
                        }

                        fr.frux.shared.domain.model.NotificationSubjectType.TREE -> {
                            navigationState.navigate(Route.TreeDetail(subjectId))
                        }

                        // Digest de maturité (#253/#157) : pas de sujet unique (subjectId = 0, M3),
                        // le tap ouvre l'onglet Favoris plutôt qu'une fiche.
                        fr.frux.shared.domain.model.NotificationSubjectType.DIGEST -> {
                            navigationState.replaceWith(Route.Favorites)
                        }

                        // Session révoquée (#295) : l'alerte se lit dans la liste, il n'y a pas
                        // d'écran de sessions à ouvrir (hors périmètre, ADR 0019).
                        fr.frux.shared.domain.model.NotificationSubjectType.ACCOUNT -> {
                            Unit
                        }

                        // Suggestion d'espèce tranchée (#333) : pas de fiche client à ouvrir, le
                        // texte de la notification porte déjà toute l'information utile.
                        fr.frux.shared.domain.model.NotificationSubjectType.SPECIES_SUGGESTION -> {
                            Unit
                        }

                        // Retour traité (#586) et compte signalé (#588) : rien à ouvrir, le texte
                        // porte toute l'information.
                        fr.frux.shared.domain.model.NotificationSubjectType.FEEDBACK,
                        fr.frux.shared.domain.model.NotificationSubjectType.USER_REPORT,
                        -> {
                            Unit
                        }

                        // Filtré en amont par le mapper, jamais atteint en pratique. No-op défensif
                        // pour rester exhaustif (#253/#157, résilience aux kinds futurs).
                        fr.frux.shared.domain.model.NotificationSubjectType.UNKNOWN -> {
                            Unit
                        }
                    }
                },
            )
        }

        entry<Route.Feedback>(metadata = fullZoneMetadata) { key ->
            // Sous-page du Profil (#437), sans barre du bas, comme Réglages.
            fr.frux.feature.feedback.FeedbackScreen(
                sourceScreen = key.sourceScreen,
                onBack = { navigationState.navigateBack() },
            )
        }

        entry<Route.Premium>(metadata = fullZoneMetadata) {
            // Sous-page du Profil (#456), sans barre du bas, comme Réglages.
            fr.frux.feature.premium
                .PremiumScreen(onBack = { navigationState.navigateBack() })
        }

        entry<Route.NotificationPreferences>(metadata = fullZoneMetadata) {
            fr.frux.feature.notifications.NotificationPreferencesScreen(
                onBack = { navigationState.navigateBack() },
                onOpenPremium = { navigationState.navigate(Route.Premium) },
            )
        }

        entry<Route.BlockedUsers>(metadata = fullZoneMetadata) {
            // Sous-page du Profil (#515), sans barre du bas, comme Réglages.
            fr.frux.feature.blockedusers
                .BlockedUsersScreen(onBack = { navigationState.navigateBack() })
        }

        entry<Route.TreeList>(metadata = mapPanelMetadata) { route ->
            fr.frux.feature.treelist.TreeListScreen(
                origin =
                    fr.frux.feature.treelist.TreeListOrigin(
                        latitude = route.latitude,
                        longitude = route.longitude,
                        radiusKm = route.radiusKm,
                        userLatitude = route.userLatitude,
                        userLongitude = route.userLongitude,
                    ),
                onNavigateToTreeDetail = { treeId ->
                    navigationState.navigate(Route.TreeDetail(treeId))
                },
                onBack = {
                    navigationState.navigateBack()
                },
                onAddTreeClick = {
                    navigationState.navigate(addTreeRouteFrom(mapHost?.camera?.position, AddTreeOrigin.UNKNOWN))
                },
            )
        }

        entry<Route.Profile>(metadata = tabSwitchMetadata + wideFullZoneMetadata) {
            fr.frux.feature.profile.ProfileScreen(
                onOpenSettings = {
                    navigationState.navigate(Route.Settings)
                },
                onOpenMyTrees = { navigationState.navigate(Route.MyTrees) },
                onOpenNotifications = { navigationState.navigate(Route.Notifications) },
                onOpenFeedback = {
                    navigationState.navigate(
                        Route.Feedback(sourceScreen = fr.frux.feature.feedback.FEEDBACK_SOURCE_PROFILE),
                    )
                },
                onOpenPremium = { navigationState.navigate(Route.Premium) },
                onOpenBlockedUsers = { navigationState.navigate(Route.BlockedUsers) },
                onAddTree = {
                    navigationState.navigate(addTreeRouteFrom(null, AddTreeOrigin.UNKNOWN))
                },
                // Pas de rappel de déconnexion : l'écran change l'état de session, et c'est App qui
                // navigue en réaction (#48). Un seul chemin de sortie de session, que celle-ci soit
                // quittée volontairement ou expirée par le serveur.
            )
        }

        // ============================================================================
        // DETAIL SCREENS (with arguments)
        // ============================================================================

        entry<Route.TreeDetail>(metadata = mapPanelMetadata) { key ->
            // Même raison qu'à l'entrée Map ci-dessus : un launcher par entrée, pas par tap.
            val directionsLauncher = remember { createDirectionsLauncher() }
            val shareLauncher = remember { createShareLauncher() }
            val deletedMessage = stringResource(Res.string.nav_tree_deleted)
            // Type-safe access to route arguments via `key` parameter
            fr.frux.feature.treedetail.TreeDetailScreen(
                treeId = key.treeId,
                onBack = {
                    navigationState.navigateBack()
                },
                onTreeDeleted = {
                    mapHost?.viewModel?.announceTreeRemoved(key.treeId, deletedMessage)
                    navigationState.resetTo(Route.Map)
                },
                onCreatorClick = { userId ->
                    navigationState.navigate(Route.UserProfile(userId))
                },
                onStartHarvest = {
                    navigationState.navigate(Route.CreateHarvest(treeId = key.treeId))
                },
                onMoveTree = { args ->
                    navigationState.navigate(
                        Route.MoveTree(
                            treeId = args.treeId,
                            latitude = args.latitude,
                            longitude = args.longitude,
                            speciesName = args.speciesName,
                            scientificName = args.scientificName,
                            canReportGone = args.canReportGone,
                        ),
                    )
                },
                onMapPreviewClick = { latitude, longitude ->
                    // #323 : même mécanisme que l'ajout d'arbre (focusOnNewTree) pour parler à la
                    // carte permanente (ADR 0009), sans message de confirmation ici.
                    mapHost?.viewModel?.focusOnNewTree(
                        NewTreeFocus(
                            target =
                                fr.frux.core.maps
                                    .Location(latitude, longitude),
                            treeId = key.treeId,
                        ),
                    )
                    navigationState.navigate(Route.Map)
                },
                onHarvestClick = { harvestId -> navigationState.navigate(Route.HarvestDetail(harvestId)) },
                onOpenSite = { siteId -> navigationState.navigate(Route.SiteDetail(siteId)) },
                // Anonyme sur une action réservée (#264) : invite snackbar sur la fiche, suivie ici.
                onLogin = { navigationState.requestLogin(Route.TreeDetail(key.treeId)) },
                onDirections = { latitude, longitude, label ->
                    directionsLauncher.openDirections(latitude, longitude, label)
                },
                onShare = { speciesName ->
                    // Chemin public de la fiche (#299) : construit ici, pas dans le module de
                    // partage, pour ne pas dupliquer la route déjà décrite par RoutePaths.
                    // TreeDetail a toujours un chemin (cf. Route.toUrlPath) : `!!` sûr ici.
                    val url = webAppBaseUrl() + Route.TreeDetail(key.treeId).toUrlPath()!!
                    val content = buildTreeShareContent(speciesName, url)
                    shareLauncher.share(content.title, content.text, content.url)
                },
                onTreeLocated = { latitude, longitude ->
                    mapHost?.viewModel?.revealTree(
                        treeId = key.treeId,
                        target =
                            fr.frux.core.maps
                                .Location(latitude, longitude),
                    )
                },
            )
        }

        entry<Route.SiteDetail>(metadata = mapPanelMetadata) { key ->
            val directionsLauncher = remember { createDirectionsLauncher() }
            fr.frux.feature.site.detail.SiteDetailScreen(
                siteId = key.siteId,
                onBack = { navigationState.navigateBack() },
                onTreeClick = { treeId -> navigationState.navigate(Route.TreeDetail(treeId)) },
                onLogin = { navigationState.requestLogin(Route.SiteDetail(key.siteId)) },
                onDirections = { latitude, longitude, label ->
                    directionsLauncher.openDirections(latitude, longitude, label)
                },
                onDeleted = {
                    mapHost?.sites?.announceRemoved(key.siteId)
                    navigationState.navigateBack()
                },
                onStartHarvest = { siteId -> navigationState.navigate(Route.CreateHarvest(siteId = siteId)) },
                onHarvestClick = { harvestId -> navigationState.navigate(Route.HarvestDetail(harvestId)) },
            )
        }

        entry<Route.HarvestDetail>(metadata = mapPanelMetadata) { key ->
            fr.frux.feature.harvestdetail.HarvestDetailScreen(
                harvestId = key.harvestId,
                onBack = { navigationState.navigateBack() },
                onTreeClick = { treeId -> navigationState.navigate(Route.TreeDetail(treeId)) },
                onSiteClick = { siteId -> navigationState.navigate(Route.SiteDetail(siteId)) },
                onLogin = { navigationState.requestLogin(Route.HarvestDetail(key.harvestId)) },
            )
        }

        entry<Route.UserProfile>(metadata = mapPanelMetadata) { key ->
            fr.frux.feature.userprofile.UserProfileScreen(
                userId = key.userId,
                onBack = { navigationState.navigateBack() },
            )
        }

        entry<Route.AddTree>(metadata = mapPanelMetadata) { key ->
            val photoUploadFailedMessage = stringResource(Res.string.nav_addtree_photo_upload_failed)
            val treeAddedMessage = stringResource(Res.string.nav_addtree_confirmation)
            val firstHereMessage = stringResource(Res.string.nav_addtree_confirmation_first_here)
            // `mapHost` est non nul sur cette route (mapEverNeeded dans App).
            val host = checkNotNull(mapHost) { "La carte persistante est requise par Route.AddTree" }
            val aimScope = rememberCoroutineScope()
            val aim =
                remember(host, aimScope) {
                    host.aim.asWizardAim(aimScope) { navigationState.backStack.lastOrNull() is Route.AddTree }
                }
            // Android 16+ : le geste prédictif anime déjà un pop vers la scène précédente. Quand
            // l'assistant intercepte (étape 2 ou 3, dialogue), un handler plus prioritaire que celui
            // de NavDisplay (composé après lui) évite cette animation de sortie suivie d'un rebond.
            val interceptors = LocalNavigationBackInterceptors.current
            NavigationBackHandler(
                state = rememberNavigationEventState(currentInfo = NavigationEventInfo.None),
                isBackEnabled = interceptors?.hasHandlers == true,
                onBackCompleted = { handleNavBack(interceptors, navigationState) },
            )
            fr.frux.feature.treeadd.wizard.TreeWizardScreen(
                start =
                    fr.frux.feature.treeadd.wizard
                        .TreeWizardStart(guideAlreadyRead = key.guideRead),
                aim = aim,
                locateButton = {
                    fr.frux.feature.map.aim
                        .AimLocateButton(host)
                },
                onExit = { navigationState.navigateBack() },
                onTreeAdded = { tree, photoUploadFailed, firstContribution ->
                    // La visée s'arrête AVANT le focus : les MapEvent émis pendant la visée sont
                    // consommés et perdus. Les libellés sont capturés en composition (#176) : ce
                    // callback est invoqué hors composition.
                    val aimZoom = host.camera.position.zoom
                    host.aim.stop()
                    host.viewModel.focusOnNewTree(
                        NewTreeFocus(
                            target =
                                fr.frux.core.maps
                                    .Location(tree.latitude, tree.longitude),
                            treeId = tree.id,
                            zoom = aimZoom,
                            confirmation = if (photoUploadFailed) photoUploadFailedMessage else treeAddedMessage,
                            // L'échec de photo prime (#502) : il demande une action, le premier arbre non.
                            firstHereConfirmation = if (photoUploadFailed) null else firstHereMessage,
                            firstContribution = firstContribution,
                            photoUploadFailed = photoUploadFailed,
                        ),
                    )
                    navigationState.returnToMapAfterCreation()
                },
                // « C'est celui-ci » sur la garde anti-doublon (#395) : la création est
                // abandonnée, la fiche de l'arbre existant remplace l'assistant dans la pile.
                onExistingTreeSelected = { treeId -> navigationState.replaceWith(Route.TreeDetail(treeId)) },
                // « Un lieu » : la création de lieu s'empile sur l'assistant, la visée s'arrête avant
                // (le formulaire ne compose pas avec la carte en visée). La flèche retour du
                // formulaire ramène ainsi à l'étape 1 et à ses tuiles, pas à la carte (#652).
                onSiteChosen = { position ->
                    host.aim.stop()
                    navigationState.navigate(Route.CreateSite(position.latitude, position.longitude))
                },
            )
        }

        entry<Route.CreateSite>(metadata = mapPanelMetadata) { key ->
            val host = checkNotNull(mapHost) { "La carte persistante est requise par Route.CreateSite" }
            // Même garde que l'assistant : quand le formulaire intercepte le retour (saisie, dialogue),
            // le geste prédictif ne doit pas animer un pop qui n'aura pas lieu.
            val interceptors = LocalNavigationBackInterceptors.current
            NavigationBackHandler(
                state = rememberNavigationEventState(currentInfo = NavigationEventInfo.None),
                isBackEnabled = interceptors?.hasHandlers == true,
                onBackCompleted = { handleNavBack(interceptors, navigationState) },
            )
            fr.frux.feature.site.create.CreateSiteScreen(
                args =
                    fr.frux.feature.site.create
                        .CreateSiteArgs(key.latitude, key.longitude),
                // La soumission reste « en cours » après la création : l'écran se retire sans
                // condition, la carte sélectionne le lieu neuf.
                onCreated = { site ->
                    host.focusOnSite(site)
                    navigationState.returnToMapAfterCreation()
                },
                onExit = { navigationState.navigateBack() },
            )
        }

        entry<Route.MoveTree>(metadata = mapPanelMetadata) { key ->
            // `mapHost` est non nul sur cette route (mapEverNeeded dans App).
            val host = checkNotNull(mapHost) { "La carte persistante est requise par Route.MoveTree" }
            val aimScope = rememberCoroutineScope()
            val aimMemory = rememberMoveAimMemory()
            val aim =
                remember(host, aimScope, aimMemory) {
                    host.aim.asMoveAim(aimScope, aimMemory) { navigationState.backStack.lastOrNull() is Route.MoveTree }
                }
            fr.frux.feature.treedetail.move.MoveTreeScreen(
                args = key.toArgs(),
                aim = aim,
                locateButton = {
                    fr.frux.feature.map.aim
                        .AimLocateButton(host)
                },
                // La visée s'arrête avant de quitter l'écran : la fiche retrouvée recadre la carte
                // par MapEvent, perdu tant que la visée tient.
                onDone = {
                    host.aim.stop()
                    navigationState.navigateBack()
                },
                // L'assistant reprend la caméra telle quelle (visée sans centre) : l'épingle reste
                // sur le point visé.
                onAddHere = { target ->
                    host.aim.stop()
                    navigationState.replaceWith(
                        Route.AddTree(target.latitude, target.longitude, origin = AddTreeOrigin.UNKNOWN),
                    )
                },
                onOpenTree = { treeId ->
                    host.aim.stop()
                    navigationState.replaceWith(Route.TreeDetail(treeId))
                },
            )
        }

        entry<Route.CreateHarvest>(metadata = mapPanelMetadata) { key ->
            fr.frux.feature.harvestcreate.CreateHarvestScreen(
                target = key.target(),
                onClose = { navigationState.navigateBack() },
                onHarvestCreated = { harvest ->
                    // Remplace le formulaire dans la pile : le retour depuis la fiche récolte ne doit
                    // pas le rouvrir (spec #167).
                    navigationState.replaceWith(Route.HarvestDetail(harvest.id))
                },
            )
        }

        // ============================================================================
        // DEV / SHOWCASE
        // ============================================================================

        entry<Route.Showcase>(metadata = fullZoneMetadata) {
            fr.frux.showcase.ShowcaseScreen(onBack = { navigationState.navigateBack() })
        }
    }
